// The Square's built places, drawn in a three-quarter storybook view. Each
// landmark returns:
//   sprites    — upright things, painted around a base point and depth-sorted
//                against walkers (so you can stand behind the cottage's fence
//                or between the cinema's benches)
//   decals     — flat things painted into the ground layer (patios, ash)
//   footprints — rectangles/circles nobody can walk through
//   lights     — warm spots that glow after dark
import { rng, inkPoly, inkRect, inkEllipse, line, roundRect, softShadow, hatch, mix } from "./paint"
import { paintLog, paintFlowerClump, paintBush } from "./nature"

const HAND = '"Bradley Hand", "Segoe Print", "Chalkboard SE", "Comic Neue", cursive'

export function buildLandmarks(byKey) {
    const out = { sprites: [], decals: [], footprints: [], lights: [], anchors: anchorsFor(byKey) }
    const add = (part) => {
        out.sprites.push(...(part.sprites || []))
        out.decals.push(...(part.decals || []))
        out.footprints.push(...(part.footprints || []))
        out.lights.push(...(part.lights || []))
    }
    if (byKey.porch) add(cottage(byKey.porch))
    if (byKey.radio) add(radioShack(byKey.radio))
    if (byKey.screen) add(cinema(byKey.screen))
    if (byKey.campfire) add(campfire(byKey.campfire))
    if (byKey.signpost) add(signpost(byKey.signpost))
    if (byKey.stage) add(stage(byKey.stage))
    if (byKey.poker) add(pokerTable(byKey.poker))
    if (byKey.doodles) add(doodleWall(byKey.doodles))
    if (byKey.board) add(noticeboard(byKey.board))
    return out
}

// Where the moving details attach, in world coordinates, so the motion layer
// (community_square/ambient.js) can puff smoke from the right chimney or play
// a film on the right screen. Kept next to the painters they must agree with.
function anchorsFor(byKey) {
    const a = {}
    const porch = byKey.porch, radio = byKey.radio, screen = byKey.screen, stageL = byKey.stage
    if (porch) a.chimney = { x: porch.x + 79, y: porch.y - 8 - 150 + 16 }
    if (radio) a.radio = { x: radio.x, y: radio.y - 46 }
    if (screen) {
        const base = screen.y - 58
        a.screen = { x: screen.x - 80, y: base - 100, w: 160, h: 66 }
        a.projector = { x: screen.x + 104, y: screen.y + 70 - 40 }
        a.stringLights = Array.from({ length: 11 }, (_, i) => {
            const t = (i + 1) / 12
            return { x: screen.x - 92 + 184 * t, y: base - 116 + 2 * t * (1 - t) * 20 + 5 }
        })
    }
    if (stageL) {
        const front = stageL.y + 18
        a.spotlights = [{ x: stageL.x - 150 + 9, y: front + 4 - 46, dir: 1 }, { x: stageL.x + 150 - 9, y: front + 4 - 46, dir: -1 }]
        a.stage = { x: stageL.x, y: stageL.y - 30 }
    }
    if (byKey.campfire) a.fire = { x: byKey.campfire.x, y: byKey.campfire.y }
    return a
}

// ---- Jane's cottage ----------------------------------------------------------

function cottage(l) {
    const baseY = l.y - 8
    const x0 = l.x - 88, x1 = l.x + 88, wallTop = baseY - 70, apexY = baseY - 150
    const depth = 34
    const sprites = [{
        x: l.x, y: baseY, box: [-110, -170, 150, 8],
        paint(ctx, pal) {
            const ink = pal.ink
            const X = (x) => x - l.x, Y = (y) => y - baseY
            // side wall and back roof slope (the three-quarter depth)
            inkPoly(ctx, [[X(x1), Y(baseY)], [X(x1 + depth), Y(baseY - depth * 0.45)], [X(x1 + depth), Y(wallTop - depth * 0.45)], [X(x1), Y(wallTop)]], pal.wallShade, ink)
            inkPoly(ctx, [[X(l.x), Y(apexY)], [X(l.x + depth), Y(apexY - depth * 0.45)], [X(x1 + depth + 14), Y(wallTop - depth * 0.45 + 6)], [X(x1 + 14), Y(wallTop + 6)]], pal.roofDark, ink)
            shingles(ctx, pal, [[X(l.x), Y(apexY)], [X(l.x + depth), Y(apexY - depth * 0.45)], [X(x1 + depth + 14), Y(wallTop - depth * 0.45 + 6)], [X(x1 + 14), Y(wallTop + 6)]], pal.roof, 7)
            // chimney on the back slope
            inkRect(ctx, X(x1 - 18), Y(apexY + 22), 18, 36, pal.stoneDark, ink)
            inkRect(ctx, X(x1 - 21), Y(apexY + 18), 24, 7, pal.stone, ink)
            // front wall and gable
            inkPoly(ctx, [[X(x0), Y(baseY)], [X(x1), Y(baseY)], [X(x1), Y(wallTop)], [X(l.x), Y(apexY + 6)], [X(x0), Y(wallTop)]], pal.wall, ink, { line: 1.8 })
            ctx.save()
            ctx.beginPath(); ctx.moveTo(X(x0), Y(baseY)); ctx.lineTo(X(x1), Y(baseY)); ctx.lineTo(X(x1), Y(wallTop)); ctx.lineTo(X(l.x), Y(apexY + 6)); ctx.lineTo(X(x0), Y(wallTop)); ctx.closePath(); ctx.clip()
            hatch(ctx, [X(x0), Y(apexY), 180, 150], pal.wallShade, { seed: 3, angle: -0.3, gap: 5, alpha: 0.35 })
            ctx.restore()
            // timber frame on the gable
            line(ctx, [[X(x0), Y(wallTop)], [X(x1), Y(wallTop)]], pal.timber, 3.5)
            line(ctx, [[X(l.x), Y(apexY + 8)], [X(l.x), Y(wallTop)]], pal.timber, 3)
            line(ctx, [[X(l.x - 40), Y(wallTop - 2)], [X(l.x - 12), Y(apexY + 36)]], pal.timber, 2.5)
            line(ctx, [[X(l.x + 40), Y(wallTop - 2)], [X(l.x + 12), Y(apexY + 36)]], pal.timber, 2.5)
            // round attic window
            inkEllipse(ctx, X(l.x), Y(apexY + 48), 11, 11, pal.night ? pal.windowLit : pal.window, ink, 1.6)
            line(ctx, [[X(l.x - 11), Y(apexY + 48)], [X(l.x + 11), Y(apexY + 48)]], pal.timber, 1.5)
            // front roof edges (overhang) with a shingle band
            const left = [[X(l.x), Y(apexY - 4)], [X(x0 - 18), Y(wallTop + 10)], [X(x0 - 12), Y(wallTop + 20)], [X(l.x), Y(apexY + 12)]]
            const right = [[X(l.x), Y(apexY - 4)], [X(x1 + 18), Y(wallTop + 10)], [X(x1 + 12), Y(wallTop + 20)], [X(l.x), Y(apexY + 12)]]
            inkPoly(ctx, left, pal.roof, ink, { line: 1.8 })
            inkPoly(ctx, right, pal.roof, ink, { line: 1.8 })
            line(ctx, [[X(l.x), Y(apexY + 2)], [X(x0 - 14), Y(wallTop + 15)]], pal.roofLight, 2, 0.8)
            // door with an arched top, a step and a lantern
            const dw = 32, dh = 50
            ctx.beginPath()
            ctx.moveTo(-dw / 2, 0); ctx.lineTo(-dw / 2, -dh + dw / 2); ctx.arc(0, -dh + dw / 2, dw / 2, Math.PI, 0); ctx.lineTo(dw / 2, 0); ctx.closePath()
            ctx.fillStyle = pal.door; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1.8; ctx.stroke()
            line(ctx, [[0, -dh + 6], [0, -2]], pal.doorDark, 1.5)
            ctx.fillStyle = "#E8C45A"; ctx.beginPath(); ctx.arc(9, -24, 2.2, 0, Math.PI * 2); ctx.fill()
            inkRect(ctx, -24, -2, 48, 8, pal.stone, ink, { line: 1.4 })
            lantern(ctx, pal, 26, -44)
            // two windows with shutters and flower boxes
            ;[-54, 54].forEach((dx) => windowWithBox(ctx, pal, dx, -44))
            return null
        }
    }]
    // picket fence either side of the patio, and the mailbox
    const fenceY = l.y + 36
    sprites.push(fence(l.x - 150, fenceY - 10, l.x - 52, fenceY, 11))
    sprites.push(fence(l.x + 52, fenceY, l.x + 150, fenceY - 10, 12))
    sprites.push({
        x: l.x + 118, y: l.y + 22, box: [-14, -46, 16, 4],
        paint(ctx, pal) {
            inkRect(ctx, -2, -30, 4, 30, pal.woodDark, pal.ink, { line: 1.2 })
            ctx.beginPath(); ctx.moveTo(-11, -30); ctx.lineTo(-11, -40); ctx.arc(0, -40, 11, Math.PI, 0); ctx.lineTo(11, -30); ctx.closePath()
            ctx.fillStyle = "#6C8FB8"; ctx.fill(); ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.5; ctx.stroke()
            inkRect(ctx, 11, -46, 3, 12, pal.flag[0], pal.ink, { line: 1 })
        }
    })
    return {
        sprites,
        decals: [(ctx, pal) => {
            flagstones(ctx, pal, l.x, l.y + 12, 96, 30, 17)
            paintFlowerClump(ctx, pal, l.x - 68, baseY + 6, 71)
            paintFlowerClump(ctx, pal, l.x + 70, baseY + 6, 72)
            paintFlowerClump(ctx, pal, l.x - 112, baseY + 2, 73)
        }],
        footprints: [{ x: x0 - 6, y: apexY + 40, w: x1 - x0 + depth + 16, h: baseY - apexY - 36 }],
        lights: [{ x: l.x - 54, y: baseY - 44, r: 70 }, { x: l.x + 54, y: baseY - 44, r: 70 }, { x: l.x + 26, y: baseY - 44, r: 55 }, { x: l.x, y: apexY + 48, r: 45 }]
    }
}

function shingles(ctx, pal, quad, color, rows) {
    ctx.save()
    ctx.beginPath(); quad.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y))); ctx.closePath(); ctx.clip()
    const [a, b, c, d] = quad
    for (let i = 1; i < rows; i++) {
        const t = i / rows
        const p = [a[0] + (d[0] - a[0]) * t, a[1] + (d[1] - a[1]) * t]
        const q = [b[0] + (c[0] - b[0]) * t, b[1] + (c[1] - b[1]) * t]
        line(ctx, [p, q], mix(color, "#000000", 0.25), 1.1, 0.7)
    }
    ctx.restore()
}

function windowWithBox(ctx, pal, cx, cy) {
    const w = 30, h = 26
    inkRect(ctx, cx - w / 2 - 9, cy - h / 2, 8, h, pal.flag[2], pal.ink, { line: 1.2 })
    inkRect(ctx, cx + w / 2 + 1, cy - h / 2, 8, h, pal.flag[2], pal.ink, { line: 1.2 })
    inkRect(ctx, cx - w / 2, cy - h / 2, w, h, pal.night ? pal.windowLit : pal.window, pal.ink, { line: 1.6 })
    if (!pal.night) {
        ctx.fillStyle = "rgba(255,255,255,0.55)"
        ctx.beginPath(); ctx.moveTo(cx - w / 2 + 3, cy + h / 2 - 3); ctx.lineTo(cx - w / 2 + 3, cy - h / 2 + 3); ctx.lineTo(cx - 2, cy - h / 2 + 3); ctx.closePath(); ctx.fill()
    }
    line(ctx, [[cx, cy - h / 2], [cx, cy + h / 2]], pal.timber, 2)
    line(ctx, [[cx - w / 2, cy], [cx + w / 2, cy]], pal.timber, 2)
    inkRect(ctx, cx - w / 2 - 4, cy + h / 2, w + 8, 8, pal.woodDark, pal.ink, { line: 1.3 })
    const r = rng(cx + 99)
    for (let i = 0; i < 8; i++) {
        ctx.fillStyle = i % 3 === 0 ? pal.leaf : pal.flowers[Math.floor(r() * pal.flowers.length)]
        ctx.beginPath(); ctx.arc(cx - w / 2 + 2 + i * 4.3, cy + h / 2 - 1 - r() * 3, 2.6, 0, Math.PI * 2); ctx.fill()
    }
}

function lantern(ctx, pal, x, y) {
    line(ctx, [[x, y - 10], [x, y - 4]], pal.ink, 1.4)
    inkPoly(ctx, [[x - 5, y - 4], [x + 5, y - 4], [x + 6, y + 8], [x - 6, y + 8]], pal.night ? pal.lamp : "#FFF1C4", pal.ink, { line: 1.3 })
    inkPoly(ctx, [[x - 7, y - 6], [x + 7, y - 6], [x, y - 11]], pal.metal, pal.ink, { line: 1.1 })
}

function flagstones(ctx, pal, x, y, rx, ry, seed) {
    const r = rng(seed)
    ctx.save()
    ctx.beginPath(); ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2); ctx.clip()
    ctx.fillStyle = mix(pal.stoneDark, pal.ground, 0.4)
    ctx.fillRect(x - rx, y - ry, rx * 2, ry * 2)
    for (let gy = -ry; gy < ry; gy += 13) {
        for (let gx = -rx; gx < rx; gx += 18) {
            const cx = x + gx + (r() - 0.5) * 5 + ((gy / 13) % 2 ? 9 : 0), cy = y + gy + (r() - 0.5) * 3
            const pts = Array.from({ length: 6 }, (_, i) => {
                const a = (i / 6) * Math.PI * 2
                return [cx + Math.cos(a) * (7 + r() * 2), cy + Math.sin(a) * (5 + r() * 1.5)]
            })
            inkPoly(ctx, pts, r() < 0.5 ? pal.stone : pal.stoneLight, mix(pal.ink, pal.stoneDark, 0.5), { line: 0.9 })
        }
    }
    ctx.restore()
    ctx.strokeStyle = pal.ink; ctx.globalAlpha = 0.35; ctx.lineWidth = 1.2
    ctx.beginPath(); ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2); ctx.stroke()
    ctx.globalAlpha = 1
}

// A little picket fence from (ax, ay) to (bx, by), sorted by its lower end.
function fence(ax, ay, bx, by, seed) {
    const baseY = Math.max(ay, by)
    const midX = (ax + bx) / 2
    return {
        x: midX, y: baseY, box: [Math.min(ax, bx) - midX - 6, Math.min(ay, by) - baseY - 30, Math.max(ax, bx) - midX + 6, 4],
        paint(ctx, pal) {
            const n = Math.max(3, Math.round(Math.hypot(bx - ax, by - ay) / 11))
            const pt = (t) => [ax + (bx - ax) * t - midX, ay + (by - ay) * t - baseY]
            const [p0, p1] = [pt(0), pt(1)]
            line(ctx, [[p0[0], p0[1] - 8], [p1[0], p1[1] - 8]], pal.ink, 4.2)
            line(ctx, [[p0[0], p0[1] - 8], [p1[0], p1[1] - 8]], pal.cloth, 2.4)
            line(ctx, [[p0[0], p0[1] - 17], [p1[0], p1[1] - 17]], pal.ink, 4.2)
            line(ctx, [[p0[0], p0[1] - 17], [p1[0], p1[1] - 17]], pal.cloth, 2.4)
            for (let i = 0; i <= n; i++) {
                const [x, y] = pt(i / n)
                inkPoly(ctx, [[x - 3, y], [x - 3, y - 22], [x, y - 26], [x + 3, y - 22], [x + 3, y]], pal.cloth, pal.ink, { line: 1.2 })
            }
        }
    }
}

// ---- the radio corner --------------------------------------------------------

function radioShack(l) {
    const baseY = l.y - 6
    const sprites = [{
        x: l.x, y: baseY, box: [-80, -150, 80, 8],
        paint(ctx, pal) {
            const ink = pal.ink
            // side depth
            inkPoly(ctx, [[52, 0], [66, -8], [66, -70], [52, -62]], pal.woodDark, ink)
            // back wall with boards
            inkRect(ctx, -52, -62, 104, 62, pal.wood, ink, { line: 1.8 })
            for (let x = -52 + 13; x < 52; x += 13) line(ctx, [[x, -62], [x, 0]], pal.woodDark, 1, 0.6)
            // counter window
            inkRect(ctx, -38, -50, 76, 28, pal.screen, ink)
            // the radio on the counter
            roundRect(ctx, -22, -46, 44, 24, 6); ctx.fillStyle = "#C9704A"; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1.5; ctx.stroke()
            ctx.fillStyle = pal.cloth; roundRect(ctx, -17, -42, 20, 16, 3); ctx.fill()
            for (let i = 0; i < 4; i++) line(ctx, [[-15, -39 + i * 4], [1, -39 + i * 4]], pal.woodDark, 0.8, 0.8)
            inkEllipse(ctx, 11, -37, 5, 5, "#F2C14E", ink, 1.2)
            inkEllipse(ctx, 11, -27, 2.5, 2.5, pal.cloth, ink, 1)
            // counter shelf
            inkRect(ctx, -44, -24, 88, 6, pal.woodLight, ink, { line: 1.4 })
            // slanted roof with an awning stripe
            inkPoly(ctx, [[-64, -58], [64, -58], [74, -66], [56, -86], [-56, -86]], pal.roof, ink, { line: 1.8 })
            for (let i = 0; i < 8; i++) {
                const x = -64 + i * 16
                inkPoly(ctx, [[x, -58], [x + 16, -58], [x + 16, -52], [x + 8, -48], [x, -52]], i % 2 ? pal.cloth : pal.flag[0], ink, { line: 1 })
            }
            // antenna and flag
            line(ctx, [[30, -86], [34, -140]], pal.metal, 2)
            line(ctx, [[34, -140], [28, -126]], pal.metal, 1.4)
            line(ctx, [[34, -140], [40, -126]], pal.metal, 1.4)
            inkPoly(ctx, [[34, -140], [54, -134], [34, -128]], pal.flag[1], ink, { line: 1.1 })
            // sign
            roundRect(ctx, -34, -84, 60, 16, 4); ctx.fillStyle = pal.cloth; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1.2; ctx.stroke()
            ctx.fillStyle = pal.night ? "#2E3431" : pal.label; ctx.font = `700 11px ${HAND}`; ctx.textAlign = "center"; ctx.fillText("JANE RADIO", -4, -72)
        }
    }]
    // a speaker horn on a pole, with bunting back to the shack
    sprites.push({
        x: l.x + 96, y: l.y + 20, box: [-120, -120, 20, 6],
        paint(ctx, pal) {
            const ink = pal.ink
            line(ctx, [[0, 0], [0, -92]], pal.woodDark, 4)
            inkPoly(ctx, [[-2, -98], [14, -110], [14, -84], [-2, -92]], pal.metal, ink, { line: 1.3 })
            const from = [-160, -92], to = [0, -88]
            for (let i = 0; i < 7; i++) {
                const t = (i + 0.5) / 7
                const x = from[0] + (to[0] - from[0]) * t, y = from[1] + (to[1] - from[1]) * t + Math.sin(t * Math.PI) * 14
                inkPoly(ctx, [[x - 6, y - 3], [x + 6, y - 3], [x, y + 9]], pal.flag[i % pal.flag.length], ink, { line: 0.9 })
            }
            line(ctx, [from, [-80, -76], to], ink, 1, 0.8)
        }
    })
    sprites.push(stool(l.x - 30, l.y + 30), stool(l.x + 26, l.y + 34))
    return {
        sprites,
        decals: [(ctx, pal) => { plankDeck(ctx, pal, l.x - 70, baseY - 2, 140, 22) }],
        footprints: [{ x: l.x - 66, y: baseY - 40, w: 134, h: 42 }],
        lights: [{ x: l.x, y: baseY - 36, r: 80 }]
    }
}

function stool(x, y) {
    return {
        x, y, box: [-14, -24, 14, 4],
        paint(ctx, pal) {
            line(ctx, [[-7, 0], [-5, -14]], pal.woodDark, 2.4)
            line(ctx, [[7, 0], [5, -14]], pal.woodDark, 2.4)
            inkEllipse(ctx, 0, -16, 11, 5, pal.wood, pal.ink, 1.4)
        }
    }
}

function plankDeck(ctx, pal, x, y, w, h) {
    inkRect(ctx, x, y, w, h, pal.woodLight, pal.ink, { line: 1.3 })
    for (let px = x + 14; px < x + w; px += 14) line(ctx, [[px, y], [px, y + h]], pal.woodDark, 1, 0.6)
}

// ---- the outdoor cinema -------------------------------------------------------

function cinema(l) {
    const screenBase = l.y - 58
    const sprites = [{
        x: l.x, y: screenBase, box: [-110, -130, 110, 6],
        paint(ctx, pal) {
            const ink = pal.ink
            // posts
            ;[-92, 92].forEach((x) => { inkRect(ctx, x - 5, -112, 10, 112, pal.woodDark, ink, { line: 1.4 }) })
            // string lights between the post tops
            ctx.save()
            ctx.strokeStyle = ink; ctx.lineWidth = 1
            ctx.beginPath(); ctx.moveTo(-92, -116); ctx.quadraticCurveTo(0, -96, 92, -116); ctx.stroke()
            for (let i = 1; i < 12; i++) {
                const t = i / 12, x = -92 + 184 * t, y = -116 + 2 * t * (1 - t) * 20 * 1
                ctx.fillStyle = i % 2 ? pal.lamp : pal.flag[1]
                ctx.beginPath(); ctx.arc(x, y + 5, 3, 0, Math.PI * 2); ctx.fill()
            }
            ctx.restore()
            // the screen: a stretched canvas with a soft sag
            ctx.beginPath()
            ctx.moveTo(-84, -104); ctx.quadraticCurveTo(0, -100, 84, -104); ctx.lineTo(84, -30); ctx.quadraticCurveTo(0, -34, -84, -30); ctx.closePath()
            const g = ctx.createLinearGradient(0, -104, 0, -30)
            g.addColorStop(0, pal.night ? "#DDE6EE" : pal.cloth); g.addColorStop(1, pal.night ? "#B8C7D2" : pal.clothShade)
            ctx.fillStyle = g; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 2; ctx.stroke()
            // a play symbol, as if a film is waiting
            ctx.fillStyle = pal.night ? "#8FA3B0" : "#E0A43A"
            ctx.beginPath(); ctx.moveTo(-9, -80); ctx.lineTo(13, -67); ctx.lineTo(-9, -54); ctx.closePath(); ctx.fill()
            line(ctx, [[-84, -30], [84, -30]], pal.woodDark, 3)
        }
    }]
    // three rows of benches either side of an aisle
    const rows = [0, 1, 2].map((row) => l.y - 8 + row * 38)
    rows.forEach((y) => { sprites.push(bench(l.x - 40, y, 64), bench(l.x + 40, y, 64)) })
    sprites.push({
        x: l.x + 104, y: l.y + 70, box: [-20, -44, 20, 4],
        paint(ctx, pal) {
            line(ctx, [[-10, 0], [0, -24]], pal.metal, 2)
            line(ctx, [[10, 0], [0, -24]], pal.metal, 2)
            line(ctx, [[0, 0], [0, -24]], pal.metal, 2)
            inkRect(ctx, -12, -38, 24, 16, "#4A4F52", pal.ink, { line: 1.4 })
            inkEllipse(ctx, -6, -40, 5, 5, "#6E7477", pal.ink, 1.1)
            inkEllipse(ctx, 6, -40, 5, 5, "#6E7477", pal.ink, 1.1)
            inkEllipse(ctx, 0, -44 + 14, 3, 3, pal.lamp, pal.ink, 1)
        }
    })
    return {
        sprites,
        decals: [(ctx, pal) => {
            ctx.save()
            ctx.fillStyle = mix(pal.groundDark, pal.path, 0.35)
            ctx.globalAlpha = 0.6
            ctx.beginPath(); ctx.ellipse(l.x, l.y + 28, 112, 62, 0, 0, Math.PI * 2); ctx.fill()
            ctx.restore()
        }],
        footprints: [{ x: l.x - 98, y: screenBase - 8, w: 12, h: 10 }, { x: l.x + 86, y: screenBase - 8, w: 12, h: 10 }]
            .concat(rows.flatMap((y) => [{ x: l.x - 72, y: y - 8, w: 64, h: 9 }, { x: l.x + 8, y: y - 8, w: 64, h: 9 }])),
        lights: [{ x: l.x, y: screenBase - 66, r: 130 }, { x: l.x + 104, y: l.y + 40, r: 40 }]
    }
}

function bench(x, y, width) {
    return {
        x, y, box: [-width / 2 - 6, -30, width / 2 + 6, 6],
        paint(ctx, pal) {
            const w = width / 2, ink = pal.ink
            ;[-w + 8, w - 8].forEach((lx) => { line(ctx, [[lx, 0], [lx, -12]], pal.woodDark, 3) })
            inkRect(ctx, -w, -16, width, 6, pal.wood, ink, { line: 1.3 })
            ;[-w + 8, w - 8].forEach((lx) => { line(ctx, [[lx, -10], [lx, -26]], pal.woodDark, 3) })
            inkRect(ctx, -w, -28, width, 6, pal.woodLight, ink, { line: 1.3 })
        }
    }
}

function stump(x, y) {
    return {
        x, y, box: [-16, -24, 16, 4],
        paint(ctx, pal) {
            inkPoly(ctx, [[-13, 0], [-11, -16], [11, -16], [13, 0]], pal.trunk, pal.ink, { line: 1.3 })
            inkEllipse(ctx, 0, -16, 11, 5, pal.woodLight, pal.ink, 1.3)
            ctx.strokeStyle = pal.woodDark; ctx.lineWidth = 0.8
            ctx.beginPath(); ctx.ellipse(0, -16, 6, 2.6, 0, 0, Math.PI * 2); ctx.stroke()
        }
    }
}

export function lampPost(x, y) {
    return {
        x, y, box: [-16, -84, 16, 4],
        light: { x, y: y - 66, r: 80 },
        paint(ctx, pal) {
            line(ctx, [[0, 0], [0, -62]], pal.ink, 4)
            line(ctx, [[0, 0], [0, -62]], pal.metal, 2)
            inkPoly(ctx, [[-7, -62], [7, -62], [5, -74], [-5, -74]], pal.night ? pal.lamp : "#FFF1C4", pal.ink, { line: 1.3 })
            inkPoly(ctx, [[-9, -74], [9, -74], [0, -82]], pal.metal, pal.ink, { line: 1.2 })
            inkRect(ctx, -5, -4, 10, 5, pal.metal, pal.ink, { line: 1 })
        }
    }
}

function cobbles(ctx, pal, x, y, rx, ry, seed) {
    const r = rng(seed)
    ctx.save()
    ctx.beginPath(); ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2); ctx.clip()
    ctx.fillStyle = pal.stoneDark; ctx.fillRect(x - rx, y - ry, rx * 2, ry * 2)
    for (let i = 0; i < 180; i++) {
        const a = r() * Math.PI * 2, d = Math.sqrt(r())
        inkEllipse(ctx, x + Math.cos(a) * rx * d, y + Math.sin(a) * ry * d, 4 + r() * 2, 2.6 + r(), r() < 0.5 ? pal.stone : pal.stoneLight, mix(pal.ink, pal.stoneDark, 0.6), 0.7)
    }
    ctx.restore()
}

// ---- the campfire ------------------------------------------------------------

function campfire(l) {
    // log benches in a ring, each lying along the circle
    const sprites = [0.5, 1.5, 2.5, 3.3, 4.3].map((k) => {
        const a = (k / 5) * Math.PI * 2 + 0.3
        const x = l.x + Math.cos(a) * 72, y = l.y + 2 + Math.sin(a) * 40
        return { x, y, box: [-40, -30, 40, 30], paint(ctx, pal) { paintLog(ctx, pal, 26, a + Math.PI / 2) } }
    })
    return {
        sprites,
        decals: [(ctx, pal) => {
            // packed earth, ash and a ring of stones around the fire
            ctx.save()
            ctx.fillStyle = mix(pal.pathDark, pal.ground, 0.25)
            ctx.globalAlpha = 0.55
            ctx.beginPath(); ctx.ellipse(l.x, l.y + 4, 70, 40, 0, 0, Math.PI * 2); ctx.fill()
            ctx.restore()
            softShadow(ctx, l.x, l.y + 2, 34, 20, 0.5, "40,30,20")
            const r = rng(61)
            for (let i = 0; i < 14; i++) {
                const a = (i / 14) * Math.PI * 2
                const x = l.x + Math.cos(a) * 30, y = l.y + 2 + Math.sin(a) * 17
                inkEllipse(ctx, x, y, 7 + r() * 2, 5 + r(), r() < 0.5 ? pal.stone : pal.stoneLight, pal.ink, 1.2)
                ctx.fillStyle = "rgba(255,255,255,0.35)"
                ctx.beginPath(); ctx.ellipse(x - 2, y - 2, 3, 1.5, 0, 0, Math.PI * 2); ctx.fill()
            }
            line(ctx, [[l.x - 18, l.y + 8], [l.x + 18, l.y - 4]], pal.ink, 7.5)
            line(ctx, [[l.x - 18, l.y + 8], [l.x + 18, l.y - 4]], pal.trunkDark, 5.5)
            line(ctx, [[l.x - 18, l.y - 4], [l.x + 18, l.y + 8]], pal.ink, 7.5)
            line(ctx, [[l.x - 18, l.y - 4], [l.x + 18, l.y + 8]], pal.trunk, 5.5)
        }],
        footprints: [{ circle: true, x: l.x, y: l.y + 2, r: 30 }],
        lights: [{ x: l.x, y: l.y - 12, r: 170, warm: true }]
    }
}

// ---- the signpost ------------------------------------------------------------

function signpost(l) {
    const baseY = l.y + 6
    return {
        sprites: [{
            x: l.x, y: baseY, box: [-70, -110, 70, 6],
            paint(ctx, pal) {
                const ink = pal.ink
                inkRect(ctx, -5, -96, 10, 96, pal.woodDark, ink, { line: 1.5 })
                const board = (y, dir, text, color) => {
                    const w = 74, h = 18, x = dir > 0 ? -6 : 6 - w
                    const tip = dir > 0 ? x + w + 12 : x - 12
                    const pts = dir > 0
                        ? [[x, y], [x + w, y], [tip, y + h / 2], [x + w, y + h], [x, y + h]]
                        : [[x, y], [x + w, y], [x + w, y + h], [x, y + h], [tip, y + h / 2]]
                    inkPoly(ctx, pts, color, ink, { line: 1.4 })
                    ctx.fillStyle = pal.night ? "#E4ECE4" : "#2E3431"
                    ctx.font = `700 12px ${HAND}`; ctx.textAlign = "center"
                    ctx.fillText(text, x + w / 2 + (dir > 0 ? 2 : -2), y + 13)
                }
                board(-92, 1, "All apps", pal.woodLight)
                board(-68, -1, "Radio", pal.wood)
                board(-44, 1, "Campfire", pal.woodLight)
                inkPoly(ctx, [[-8, -100], [8, -100], [0, -108]], pal.roof, ink, { line: 1.2 })
            }
        }, { ...lampPost(l.x + 44, l.y + 16) }, bushSprite(l.x - 48, l.y + 18, 13, 4)],
        decals: [(ctx, pal) => {
            paintFlowerClump(ctx, pal, l.x - 16, baseY + 4, 81)
            paintFlowerClump(ctx, pal, l.x + 18, baseY + 6, 82)
        }],
        footprints: [{ x: l.x - 6, y: baseY - 6, w: 12, h: 8 }],
        lights: [{ x: l.x + 44, y: l.y - 50, r: 80 }]
    }
}

function bushSprite(x, y, s, seed) {
    return { x, y, box: [-s * 1.5, -s * 1.5, s * 1.5, 4], paint(ctx, pal) { paintBush(ctx, pal, s, seed) } }
}

// The welcome arch over the path into the Square.
export function welcomeArch(x, y) {
    return {
        x, y, box: [-80, -120, 80, 6],
        footprints: [{ x: x - 66, y: y - 6, w: 10, h: 8 }, { x: x + 56, y: y - 6, w: 10, h: 8 }],
        paint(ctx, pal) {
            const ink = pal.ink
            ;[-60, 60].forEach((px) => inkRect(ctx, px - 5, -96, 10, 96, pal.woodDark, ink, { line: 1.5 }))
            ctx.beginPath(); ctx.moveTo(-70, -84); ctx.quadraticCurveTo(0, -126, 70, -84); ctx.lineTo(70, -72); ctx.quadraticCurveTo(0, -112, -70, -72); ctx.closePath()
            ctx.fillStyle = pal.wood; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1.6; ctx.stroke()
            roundRect(ctx, -46, -104, 92, 22, 5); ctx.fillStyle = pal.cloth; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1.4; ctx.stroke()
            ctx.fillStyle = "#2E3431"; ctx.font = `700 15px ${HAND}`; ctx.textAlign = "center"; ctx.fillText("The Square", 0, -88)
            // climbing flowers on the posts
            const r = rng(5)
            for (let i = 0; i < 16; i++) {
                const px = (i % 2 ? 60 : -60) + (r() - 0.5) * 10, py = -10 - r() * 80
                ctx.fillStyle = i % 3 ? pal.leaf : pal.flowers[i % pal.flowers.length]
                ctx.beginPath(); ctx.arc(px, py, 3.2, 0, Math.PI * 2); ctx.fill()
            }
        }
    }
}

// ---- the stage ---------------------------------------------------------------
// The platform is flat (a decal) so walkers stand on it; the proscenium with
// its curtains is a sprite behind them.

function stage(l) {
    const top = l.y - 78, front = l.y + 18, halfW = 118
    const sprites = [{
        x: l.x, y: top + 2, box: [-150, -150, 150, 8],
        paint(ctx, pal) {
            const ink = pal.ink
            // pillars
            ;[-118, 118].forEach((x) => {
                inkRect(ctx, x - 9, -120, 18, 120, pal.wood, ink, { line: 1.6 })
                inkRect(ctx, x - 12, -126, 24, 8, pal.woodLight, ink, { line: 1.3 })
            })
            // backdrop
            inkRect(ctx, -109, -112, 218, 112, pal.night ? "#3B2D45" : "#5C4A78", ink, { line: 1.4 })
            // curtains, gathered at the sides
            ;[-1, 1].forEach((side) => {
                ctx.beginPath()
                const edge = side * 109, inner = side * 58
                ctx.moveTo(edge, -112); ctx.lineTo(inner, -112)
                ctx.quadraticCurveTo(side * 70, -60, side * 90, -30)
                ctx.quadraticCurveTo(side * 96, -12, edge, 0)
                ctx.closePath()
                ctx.fillStyle = pal.night ? "#8C3A3A" : "#C84B4B"; ctx.fill()
                ctx.strokeStyle = ink; ctx.lineWidth = 1.5; ctx.stroke()
                for (let i = 1; i < 5; i++) line(ctx, [[edge - side * i * 10, -110], [edge - side * (i * 6 + 6), -6]], pal.night ? "#6E2C2C" : "#A33A3A", 1.2, 0.8)
                inkEllipse(ctx, side * 90, -30, 5, 4, "#E8C45A", ink, 1)
            })
            // the arch with bunting and a star
            ctx.beginPath(); ctx.moveTo(-130, -118); ctx.quadraticCurveTo(0, -168, 130, -118); ctx.lineTo(130, -104); ctx.quadraticCurveTo(0, -152, -130, -104); ctx.closePath()
            ctx.fillStyle = pal.woodLight; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1.6; ctx.stroke()
            for (let i = 0; i < 11; i++) {
                const t = (i + 0.5) / 11, x = -120 + 240 * t, y = -112 + 4 * t * (1 - t) * -40 * 0.9 + 8
                inkPoly(ctx, [[x - 7, y], [x + 7, y], [x, y + 11]], pal.flag[i % pal.flag.length], ink, { line: 0.9 })
            }
            star(ctx, 0, -150, 10, "#F2C14E", ink)
        }
    }, {
        // the microphone, front and centre
        x: l.x, y: l.y - 18, box: [-14, -60, 14, 4],
        paint(ctx, pal) {
            line(ctx, [[-9, 0], [0, -4], [9, 0]], pal.ink, 2)
            line(ctx, [[0, -4], [0, -44]], pal.metal, 2.2)
            inkEllipse(ctx, 0, -48, 4.5, 6, "#3A4043", pal.ink, 1.1)
        }
    }, spotlight(l.x - 150, front + 4, 1), spotlight(l.x + 150, front + 4, -1)]
    return {
        sprites,
        decals: [(ctx, pal) => {
            const ink = pal.ink
            // front face, then the planked top with a rounded apron
            ctx.beginPath()
            ctx.moveTo(l.x - halfW, top); ctx.lineTo(l.x - halfW, front + 14)
            ctx.quadraticCurveTo(l.x, front + 50, l.x + halfW, front + 14); ctx.lineTo(l.x + halfW, top); ctx.closePath()
            ctx.fillStyle = pal.woodDark; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1.6; ctx.stroke()
            ctx.beginPath()
            ctx.moveTo(l.x - halfW, top); ctx.lineTo(l.x - halfW, front)
            ctx.quadraticCurveTo(l.x, front + 36, l.x + halfW, front); ctx.lineTo(l.x + halfW, top); ctx.closePath()
            ctx.fillStyle = pal.woodLight; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1.6; ctx.stroke()
            ctx.save(); ctx.clip()
            for (let y = top + 12; y < front + 40; y += 12) line(ctx, [[l.x - halfW, y], [l.x + halfW, y]], pal.wood, 1.2, 0.9)
            ctx.restore()
            // steps down the front
            ;[0, 1].forEach((i) => inkRect(ctx, l.x - 30 + i * 4, front + 30 + i * 9, 60 - i * 8, 9, pal.wood, ink, { line: 1.2 }))
        }],
        footprints: [{ x: l.x - halfW - 14, y: top - 8, w: 18, h: 12 }, { x: l.x + halfW - 4, y: top - 8, w: 18, h: 12 }],
        lights: [{ x: l.x, y: top - 40, r: 150 }, { x: l.x - 150, y: front - 30, r: 60 }, { x: l.x + 150, y: front - 30, r: 60 }]
    }
}

function spotlight(x, y, dir) {
    return {
        x, y, box: [-20, -64, 20, 4],
        paint(ctx, pal) {
            line(ctx, [[-8, 0], [0, -40], [8, 0]], pal.metal, 2)
            ctx.save()
            ctx.translate(0, -46)
            ctx.rotate(dir * -0.5)
            inkRect(ctx, -9, -7, 18, 14, "#4A4F52", pal.ink, { line: 1.3 })
            inkEllipse(ctx, dir * 9, 0, 3, 6, pal.lamp, pal.ink, 1)
            ctx.restore()
        }
    }
}

function star(ctx, x, y, r, fill, ink) {
    ctx.beginPath()
    for (let i = 0; i < 10; i++) {
        const a = -Math.PI / 2 + (i / 10) * Math.PI * 2, rad = i % 2 ? r * 0.45 : r
        const px = x + Math.cos(a) * rad, py = y + Math.sin(a) * rad
        if (i) ctx.lineTo(px, py)
        else ctx.moveTo(px, py)
    }
    ctx.closePath()
    ctx.fillStyle = fill; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1.2; ctx.stroke()
}

// ---- the poker table ---------------------------------------------------------

function pokerTable(l) {
    const chairs = [[-58, -26], [58, -26], [-50, 30], [50, 30]].map(([dx, dy]) => chair(l.x + dx, l.y + dy, dx < 0 ? 1 : -1))
    return {
        sprites: [...chairs, {
            x: l.x, y: l.y + 6, box: [-60, -52, 60, 6],
            paint(ctx, pal) {
                const ink = pal.ink
                inkPoly(ctx, [[-10, 0], [-6, -22], [6, -22], [10, 0]], pal.woodDark, ink, { line: 1.3 })
                inkEllipse(ctx, 0, -28, 52, 22, pal.woodDark, ink, 1.6)
                inkEllipse(ctx, 0, -31, 52, 22, pal.wood, ink, 1.6)
                inkEllipse(ctx, 0, -32, 44, 17, pal.night ? "#1F4B3A" : "#2E7A57", ink, 1.2)
                // cards and chip stacks
                ;[[-16, -34, -0.3], [-4, -36, 0.1], [8, -34, 0.35]].forEach(([x, y, a]) => {
                    ctx.save(); ctx.translate(x, y); ctx.rotate(a)
                    inkRect(ctx, -4, -6, 8, 11, pal.cloth, ink, { line: 0.9 })
                    ctx.fillStyle = "#C84B4B"; ctx.beginPath(); ctx.arc(0, -1, 1.6, 0, Math.PI * 2); ctx.fill()
                    ctx.restore()
                })
                ;[[-28, -28, "#C84B4B"], [24, -27, "#3E6FB0"], [26, -32, "#F2C14E"]].forEach(([x, y, c]) => {
                    for (let i = 0; i < 3; i++) inkEllipse(ctx, x, y - i * 2.5, 5, 2.2, c, ink, 0.8)
                })
            }
        }, lampPost(l.x + 84, l.y - 30)],
        decals: [(ctx, pal) => {
            ctx.save()
            ctx.fillStyle = pal.night ? "#4A3440" : "#B8574F"
            ctx.globalAlpha = 0.85
            ctx.beginPath(); ctx.ellipse(l.x, l.y + 4, 92, 46, 0, 0, Math.PI * 2); ctx.fill()
            ctx.globalAlpha = 1
            ctx.strokeStyle = pal.night ? "#6A4C5A" : "#E8C45A"; ctx.lineWidth = 3
            ctx.beginPath(); ctx.ellipse(l.x, l.y + 4, 84, 40, 0, 0, Math.PI * 2); ctx.stroke()
            ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.2
            ctx.beginPath(); ctx.ellipse(l.x, l.y + 4, 92, 46, 0, 0, Math.PI * 2); ctx.stroke()
            ctx.restore()
        }],
        footprints: [{ circle: true, x: l.x, y: l.y + 2, r: 44 }],
        lights: [{ x: l.x + 84, y: l.y - 96, r: 90 }]
    }
}

function chair(x, y, facing) {
    return {
        x, y, box: [-16, -40, 16, 4],
        paint(ctx, pal) {
            const ink = pal.ink
            line(ctx, [[-8, 0], [-8, -14]], pal.woodDark, 2.4)
            line(ctx, [[8, 0], [8, -14]], pal.woodDark, 2.4)
            inkRect(ctx, -11, -18, 22, 6, pal.wood, ink, { line: 1.2 })
            const bx = facing > 0 ? -11 : 7
            inkRect(ctx, bx, -36, 4, 20, pal.woodDark, ink, { line: 1.1 })
            inkRect(ctx, bx - (facing > 0 ? 0 : 0), -36, 4, 4, pal.woodLight, ink, { line: 0.9 })
        }
    }
}

// ---- the doodle wall ---------------------------------------------------------

function doodleWall(l) {
    const baseY = l.y - 22
    return {
        sprites: [{
            x: l.x, y: baseY, box: [-120, -110, 120, 8],
            paint(ctx, pal) {
                const ink = pal.ink
                ;[-104, 104].forEach((x) => inkRect(ctx, x - 6, -96, 12, 96, pal.woodDark, ink, { line: 1.4 }))
                inkRect(ctx, -100, -92, 200, 70, pal.woodLight, ink, { line: 1.8 })
                inkRect(ctx, -94, -86, 188, 58, pal.night ? "#D8D6CC" : "#FBF8EE", ink, { line: 1.2 })
                // doodles in crayon colors
                const c = pal.flag
                ctx.lineCap = "round"; ctx.lineJoin = "round"
                // a sun
                ctx.strokeStyle = c[1]; ctx.lineWidth = 2.4
                ctx.beginPath(); ctx.arc(-66, -64, 9, 0, Math.PI * 2); ctx.stroke()
                for (let i = 0; i < 8; i++) { const a = (i / 8) * Math.PI * 2; line(ctx, [[-66 + Math.cos(a) * 13, -64 + Math.sin(a) * 13], [-66 + Math.cos(a) * 17, -64 + Math.sin(a) * 17]], c[1], 2) }
                // a heart
                ctx.fillStyle = c[0]
                ctx.beginPath(); ctx.moveTo(-26, -46); ctx.bezierCurveTo(-44, -58, -36, -76, -26, -66); ctx.bezierCurveTo(-16, -76, -8, -58, -26, -46); ctx.fill()
                // a stick figure waving
                ctx.strokeStyle = "#3E6FB0"; ctx.lineWidth = 2
                ctx.beginPath(); ctx.arc(16, -70, 5, 0, Math.PI * 2); ctx.stroke()
                line(ctx, [[16, -65], [16, -50]], "#3E6FB0", 2)
                line(ctx, [[16, -60], [8, -54]], "#3E6FB0", 2)
                line(ctx, [[16, -60], [25, -70]], "#3E6FB0", 2)
                line(ctx, [[16, -50], [10, -40]], "#3E6FB0", 2)
                line(ctx, [[16, -50], [22, -40]], "#3E6FB0", 2)
                // a squiggle and a star
                line(ctx, [[40, -46], [48, -58], [56, -44], [64, -60], [72, -46]], c[2], 2.4)
                star(ctx, 76, -72, 7, c[3], ink)
                // a chalk tray with crayons
                inkRect(ctx, -86, -24, 172, 5, pal.woodDark, ink, { line: 1 })
                ;[-60, -46, -32, 30, 44].forEach((x, i) => inkRect(ctx, x, -28, 10, 4, c[i % c.length], ink, { line: 0.7 }))
            }
        }, {
            x: l.x + 110, y: l.y + 24, box: [-18, -30, 18, 4],
            paint(ctx, pal) {
                // paint buckets
                ;[[-8, "#3E6FB0"], [8, "#C84B4B"]].forEach(([dx, color]) => {
                    inkPoly(ctx, [[dx - 7, 0], [dx - 8, -14], [dx + 8, -14], [dx + 7, 0]], pal.metal, pal.ink, { line: 1.1 })
                    inkEllipse(ctx, dx, -14, 8, 3, color, pal.ink, 1)
                })
            }
        }],
        decals: [(ctx, pal) => {
            // chalk and paint spatters on the ground
            const r = rng(91)
            for (let i = 0; i < 26; i++) {
                ctx.fillStyle = pal.flag[i % pal.flag.length]
                ctx.globalAlpha = 0.5
                ctx.beginPath(); ctx.arc(l.x + (r() - 0.5) * 180, l.y + 4 + (r() - 0.5) * 50, 1.5 + r() * 2.5, 0, Math.PI * 2); ctx.fill()
            }
            ctx.globalAlpha = 1
        }],
        footprints: [{ x: l.x - 112, y: baseY - 8, w: 224, h: 10 }],
        lights: [{ x: l.x, y: baseY - 60, r: 110 }]
    }
}

// ---- the noticeboard ---------------------------------------------------------

function noticeboard(l) {
    const baseY = l.y - 18
    return {
        sprites: [{
            x: l.x, y: baseY, box: [-80, -124, 80, 8],
            paint(ctx, pal) {
                const ink = pal.ink
                ;[-54, 54].forEach((x) => inkRect(ctx, x - 5, -100, 10, 100, pal.woodDark, ink, { line: 1.4 }))
                // little shingled roof
                inkPoly(ctx, [[-72, -98], [72, -98], [60, -118], [-60, -118]], pal.roof, ink, { line: 1.6 })
                shingles(ctx, pal, [[-60, -118], [60, -118], [72, -98], [-72, -98]], pal.roof, 4)
                // cork
                inkRect(ctx, -58, -94, 116, 64, pal.woodLight, ink, { line: 1.6 })
                inkRect(ctx, -52, -88, 104, 52, pal.night ? "#6E5A45" : "#C99A66", ink, { line: 1 })
                // pinned notes
                const notes = [[-44, -84, 22, 18, "#FFF2A8", -0.08], [-16, -82, 24, 20, "#FFFFFF", 0.05], [14, -86, 20, 16, "#BDE3D2", 0.1], [-38, -60, 26, 18, "#F6C8D8", 0.06], [-6, -58, 22, 18, "#FFFFFF", -0.1], [22, -62, 24, 20, "#FFF2A8", 0.04]]
                notes.forEach(([x, y, w, h, color, a], i) => {
                    ctx.save(); ctx.translate(x + w / 2, y + h / 2); ctx.rotate(a)
                    inkRect(ctx, -w / 2, -h / 2, w, h, pal.night ? mixDim(color) : color, ink, { line: 0.8 })
                    for (let k = 0; k < 3; k++) line(ctx, [[-w / 2 + 3, -h / 2 + 5 + k * 4], [w / 2 - 4 - (k === 2 ? 6 : 0), -h / 2 + 5 + k * 4]], pal.inkSoft, 0.7, 0.7)
                    ctx.fillStyle = pal.flag[i % pal.flag.length]
                    ctx.beginPath(); ctx.arc(0, -h / 2 + 2, 2, 0, Math.PI * 2); ctx.fill()
                    ctx.restore()
                })
            }
        }, stool(l.x + 64, l.y + 20)],
        decals: [(ctx, pal) => {
            paintFlowerClump(ctx, pal, l.x - 60, baseY + 6, 93)
            paintFlowerClump(ctx, pal, l.x + 40, baseY + 8, 94)
        }],
        footprints: [{ x: l.x - 62, y: baseY - 8, w: 124, h: 10 }],
        lights: [{ x: l.x, y: baseY - 60, r: 90 }]
    }
}

function mixDim(color) {
    return mix(color, "#1B2426", 0.45)
}

// Shared with the districts (community_square/districts.js), which build in
// the same hand.
export { lantern, windowWithBox, shingles, fence, stool, bench, chair, star, cobbles, flagstones, plankDeck }
